/**
 * LOUD/QUIET Button — hard-edged, uppercase, physical press.
 *
 * Hover lifts the object toward the light (translate -2,-2, shadow S->M); press pushes it
 * down into the shadow (translate 3,3, shadow collapses to 0). Ported 1:1 from the compiled
 * `_ds_bundle.js`; the bundle is the authoritative artifact wherever it disagrees with
 * `readme.md` (discrepancies flagged with NOTE below).
 *
 * Non-obvious decisions:
 * - Hover/press stay as inline JS transform handlers (as in the bundle) so the component has
 *   no stylesheet dependency; the keyboard focus ring is added the same way, via a
 *   `:focus-visible` check inside onFocus rather than a CSS rule.
 * - The bundle spreads `...rest` LAST, so a caller-supplied onMouseEnter would silently kill
 *   the lift. Here the interaction handlers are destructured out and composed (ours first,
 *   then the caller's), which is behaviourally identical unless the caller overrides them.
 * - `as="a"` + `disabled` is made genuinely inert (see below). The bundle only dims it, which
 *   leaves a "disabled" link fully clickable — a defect rather than a visual choice.
 */
/*
 * What "inert" means here, precisely, because a partial version of this shipped twice:
 * a disabled anchor behaves like `<button disabled>` — it has no href, it is out of the tab
 * order, it is announced as disabled, and *no handler a caller supplied ever runs*, in any
 * phase, for any event. That last part is not achievable by intercepting onClick: React
 * delivers `on*Capture` before `on*`, and `...rest` can carry dozens of handlers
 * (onClickCapture, onMouseDownCapture, onKeyDown, onPointerDown, onAuxClick, …). So rather
 * than racing them, every `on*` prop is removed from the props actually handed to the DOM,
 * and the component's own interaction handlers (which call the caller's) are not attached.
 * Nothing is left to fire, so nothing needs to be swallowed.
 *
 * On top of that, click is still cancelled in both phases. That is not for the caller's
 * handlers — they no longer exist — but for the page: it kills any residual default action
 * and stops the click reaching an ancestor's delegated onClick, which a real disabled control
 * would never trigger either.
 *
 * Two deliberate boundaries. (1) An *ancestor's* capture-phase handler still sees the click:
 * capture runs top-down, so it fires before this element is reached, and only
 * `pointer-events: none` could stop it — that would also drop the `cursor: not-allowed`
 * affordance, a visual regression in a component we are reproducing pixel-for-pixel.
 * (2) Key events still propagate. Nothing here acts on them (no href, no handlers, so Enter
 * on the anchor does nothing), and swallowing them would break page-level shortcuts such as
 * Escape-to-close whenever focus happened to rest on a disabled link.
 */
import * as React from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'dark';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonAs = 'button' | 'a';

/** Button renders as <button> or <a>, so it accepts the union of both attribute sets. */
type ButtonBaseAttributes = React.ButtonHTMLAttributes<HTMLButtonElement> &
  Pick<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    'href' | 'target' | 'rel' | 'download' | 'hrefLang' | 'referrerPolicy'
  >;

export interface ButtonProps extends ButtonBaseAttributes {
  children?: React.ReactNode;
  /** Fill + text colour pairing. @default "primary" */
  variant?: ButtonVariant;
  /** Height / padding / label size. @default "md" */
  size?: ButtonSize;
  /** Leading glyph. Rendered aria-hidden; keep the label meaningful without it. */
  icon?: React.ReactNode;
  /** Trailing glyph. Rendered aria-hidden. */
  iconRight?: React.ReactNode;
  /** Host element. @default "button" */
  as?: ButtonAs;
  /** @default false */
  disabled?: boolean;
  /** Stretch to the full width of the parent. @default false */
  block?: boolean;
  style?: React.CSSProperties;
}

const VARIANTS: Record<ButtonVariant, { bg: string; fg: string }> = {
  primary: { bg: 'var(--lq-yellow)', fg: 'var(--lq-ink)' },
  secondary: { bg: 'var(--lq-pink)', fg: 'var(--lq-ink)' },
  ghost: { bg: 'var(--lq-white)', fg: 'var(--lq-ink)' },
  dark: { bg: 'var(--lq-ink)', fg: 'var(--lq-paper)' },
};

// NOTE: size="md" is 48px tall in the bundle, not the 52px quoted in the spec; "sm" is 38px
// and "lg" is 58px. None of these sit on the 4px spacing scale the readme documents, and the
// paddings (9/14, 13/20, 18/26) are likewise off-scale. Bundle wins — reproduced verbatim.
const SIZES: Record<ButtonSize, { minHeight: number; padding: string; fontSize: number }> = {
  sm: { minHeight: 38, padding: '9px 14px', fontSize: 11 },
  md: { minHeight: 48, padding: '13px 20px', fontSize: 13 },
  lg: { minHeight: 58, padding: '18px 26px', fontSize: 15 },
};

// NOTE: the bundle writes the easing keyword literally rather than using the --lq-ease token.
// Kept literal so the transition never depends on a token that may not be emitted.
const TRANSITION =
  'transform var(--lq-motion-fast) ease, box-shadow var(--lq-motion-fast) ease';

// NOTE: the bundle does NOT honour prefers-reduced-motion anywhere, although the readme claims
// "everything respects prefers-reduced-motion". The lift/press state changes are preserved
// exactly; only the tween between them is suppressed, so static output is unchanged.
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** `:focus-visible` is unsupported on a few old engines; fall back to always showing the ring. */
function isFocusVisible(el: Element): boolean {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
}

/**
 * Drop every React event handler prop. Handlers are exactly the props named `on` + an
 * uppercase letter — both phases, since the capture variants are the same names with a
 * `Capture` suffix. Everything else (id, className, data-*, aria-*, …) is preserved.
 */
function withoutEventHandlers<T extends object>(props: T): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (/^on[A-Z]/.test(key)) continue;
    out[key] = value;
  }
  return out as T;
}

function physical(el: HTMLElement, transform: string, boxShadow: string): void {
  el.style.transition = prefersReducedMotion() ? 'none' : TRANSITION;
  el.style.transform = transform;
  el.style.boxShadow = boxShadow;
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  iconRight,
  as = 'button',
  disabled = false,
  block = false,
  style,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onMouseUp,
  onFocus,
  onBlur,
  ...rest
}: ButtonProps) {
  const palette = VARIANTS[variant];
  const sizes = SIZES[size];
  const Tag = as as React.ElementType;

  // `disabled` is not a real attribute on <a>, so a disabled anchor would otherwise stay
  // focusable and still follow its href. Make it genuinely inert instead (see the contract at
  // the top of this file): drop the href, take it out of the tab order, expose the state to
  // assistive tech, and cancel the click in both phases.
  const anchorDisabled = as === 'a' && disabled;
  const cancel = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const inertAnchorProps = anchorDisabled
    ? {
        href: undefined,
        role: 'link',
        'aria-disabled': true,
        tabIndex: -1,
        onClickCapture: cancel,
        onClick: cancel,
      }
    : null;

  const handleMouseEnter: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    if (!disabled) physical(e.currentTarget, 'var(--lq-lift)', 'var(--lq-shadow-m)');
    onMouseEnter?.(e);
  };
  const handleMouseLeave: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    physical(e.currentTarget, 'none', 'var(--lq-shadow-s)');
    onMouseLeave?.(e);
  };
  const handleMouseDown: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    if (!disabled) physical(e.currentTarget, 'var(--lq-press)', '0 0 0 var(--lq-ink)');
    onMouseDown?.(e);
  };
  const handleMouseUp: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    if (!disabled) physical(e.currentTarget, 'var(--lq-lift)', 'var(--lq-shadow-m)');
    onMouseUp?.(e);
  };
  const handleFocus: React.FocusEventHandler<HTMLButtonElement> = (e) => {
    if (isFocusVisible(e.currentTarget)) {
      e.currentTarget.style.outline = 'var(--lq-focus-ring)';
      e.currentTarget.style.outlineOffset = 'var(--lq-focus-offset, 3px)';
    }
    onFocus?.(e);
  };
  const handleBlur: React.FocusEventHandler<HTMLButtonElement> = (e) => {
    e.currentTarget.style.outline = '';
    e.currentTarget.style.outlineOffset = '';
    onBlur?.(e);
  };

  // A disabled anchor gets neither the caller's handlers (stripped from `rest`) nor ours
  // (which exist only to compose the caller's on top of the press/lift animation, and there
  // is no press or lift on a disabled control). `inertAnchorProps` supplies the only two
  // handlers that survive.
  const interactionProps = anchorDisabled
    ? null
    : {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onMouseDown: handleMouseDown,
        onMouseUp: handleMouseUp,
        onFocus: handleFocus,
        onBlur: handleBlur,
      };

  return (
    <Tag
      {...(anchorDisabled ? withoutEventHandlers(rest) : rest)}
      {...inertAnchorProps}
      disabled={as === 'button' ? disabled : undefined}
      style={{
        display: block ? 'flex' : 'inline-flex',
        width: block ? '100%' : 'auto',
        alignItems: 'center',
        justifyContent: icon || iconRight ? 'space-between' : 'center',
        gap: '18px',
        minHeight: sizes.minHeight,
        padding: sizes.padding,
        fontFamily: 'var(--lq-font-sans)',
        // NOTE: the uppercase label is Geist *Sans* 900, not Geist Mono, even though the readme
        // assigns uppercase UI labels to the mono face. Bundle wins.
        fontSize: sizes.fontSize,
        fontWeight: 900,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        color: palette.fg,
        background: palette.bg,
        border: 'var(--lq-border)',
        // NOTE: 8px radius, though the readme says sharp (0) by default and allows only the
        // 0 / 12px / 999px stops. Bundle wins — this is not --lq-radius-soft.
        borderRadius: '8px',
        boxShadow: 'var(--lq-shadow-s)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: TRANSITION,
        ...style,
      }}
      {...interactionProps}
    >
      {icon ? <span aria-hidden="true">{icon}</span> : null}
      <span>{children}</span>
      {iconRight ? <span aria-hidden="true">{iconRight}</span> : null}
    </Tag>
  );
}

export default Button;
